/**
 * SpriteFX — visual effects on game objects (sprites, images, rectangles).
 *
 * Hit flash, knockback, pulse, blink, etc.
 * Game-agnostic: operates on any Phaser game object with x/y/alpha/tint.
 */

declare const Phaser: any;

export interface FlashOptions {
  /** Tint color for the flash. Default 0xffffff (white). */
  tint?: number;
  /** Duration of the flash in ms. Default 120. */
  duration?: number;
}

export interface ShakeOptions {
  /** Max displacement in px. Default 4. */
  intensity?: number;
  /** Duration in ms. Default 200. */
  duration?: number;
}

export interface PulseOptions {
  /** Scale to pulse to. Default 1.15. */
  scale?: number;
  /** Duration of one pulse cycle in ms. Default 600. */
  duration?: number;
  /** Number of repeats (-1 = infinite). Default 0 (one pulse). */
  repeat?: number;
}

export interface BlinkOptions {
  /** Duration of one blink cycle in ms. Default 100. */
  interval?: number;
  /** Total number of blinks. Default 3. */
  count?: number;
}

export interface FadeOutOptions {
  /** Target alpha. Default 0.15. */
  alpha?: number;
  /** Duration in ms. Default 500. */
  duration?: number;
  /** Easing function. Default "Power2". */
  ease?: string;
  /** Callback when fade completes. */
  onComplete?: () => void;
}

export interface RagdollOptions {
  /** Y position of the ground. Default: target.y + 45. */
  groundY?: number;
  /** Horizontal knockback distance (px). Default: random 30–60. */
  knockbackX?: number;
  /** Tumble duration in ms. Default 700. */
  duration?: number;
  /** Final rotation angle (degrees). Default 90 (fallen on side). */
  angle?: number;
  /** Delay before fade-out starts (ms after tumble). Default 200. */
  fadeDelay?: number;
  /** Fade-out duration in ms. Default 400. */
  fadeDuration?: number;
  /** Callback when fully complete (after fade). */
  onComplete?: () => void;
}

/**
 * Flash a game object white (or a custom tint) briefly.
 * Works on any object with setTintFill / clearTint.
 */
export function flash(scene: any, target: any, opts?: FlashOptions): void {
  const tint = opts?.tint ?? 0xffffff;
  const duration = opts?.duration ?? 120;

  target.setTintFill(tint);
  scene.time.delayedCall(duration, () => {
    target.clearTint();
  });
}

/**
 * Shake a game object by oscillating its x position.
 * Returns the tween for chaining.
 */
export function shake(scene: any, target: any, opts?: ShakeOptions): any {
  const intensity = opts?.intensity ?? 4;
  const duration = opts?.duration ?? 200;
  const origX = target.x;

  return scene.tweens.add({
    targets: target,
    x: origX + intensity,
    duration: duration / 6,
    yoyo: true,
    repeat: 2,
    ease: "Sine.easeInOut",
    onComplete: () => { target.x = origX; },
  });
}

/**
 * Pulse a game object's scale.
 * Returns the tween for chaining or stopping.
 */
export function pulse(scene: any, target: any, opts?: PulseOptions): any {
  const scale = opts?.scale ?? 1.15;
  const duration = opts?.duration ?? 600;
  const repeat = opts?.repeat ?? 0;

  return scene.tweens.add({
    targets: target,
    scaleX: scale,
    scaleY: scale,
    duration: duration / 2,
    yoyo: true,
    repeat,
    ease: "Sine.easeInOut",
  });
}

/**
 * Fade a game object out to a low alpha (death, disappear, etc.).
 * Returns the tween for chaining.
 */
export function fadeOut(scene: any, target: any, opts?: FadeOutOptions): any {
  const alpha = opts?.alpha ?? 0.15;
  const duration = opts?.duration ?? 500;

  return scene.tweens.add({
    targets: target,
    alpha,
    duration,
    ease: opts?.ease ?? "Power2",
    onComplete: opts?.onComplete,
  });
}

/**
 * Blink a game object by toggling alpha on/off rapidly.
 */
export function blink(scene: any, target: any, opts?: BlinkOptions): void {
  const interval = opts?.interval ?? 100;
  const count = opts?.count ?? 3;

  let i = 0;
  const timer = scene.time.addEvent({
    delay: interval,
    repeat: count * 2 - 1,
    callback: () => {
      target.setAlpha(i % 2 === 0 ? 0.2 : 1);
      i++;
      if (i >= count * 2) {
        target.setAlpha(1);
        timer.remove();
      }
    },
  });
}

/**
 * Ragdoll a game object — knockback, tumble to the ground with bounce, then fade.
 * Uses parallel tweens with Bounce.easeOut for natural landing physics.
 */
export function ragdoll(scene: any, target: any, opts?: RagdollOptions): void {
  const groundY = opts?.groundY ?? target.y + 20;
  const duration = opts?.duration ?? 700;
  const knockbackX = opts?.knockbackX ?? (30 + Math.random() * 30);
  const angle = opts?.angle ?? 90;
  const fadeDelay = opts?.fadeDelay ?? 200;
  const fadeDuration = opts?.fadeDuration ?? 400;
  const onComplete = opts?.onComplete;

  // Flash white on death hit
  target.setTintFill(0xffffff);
  scene.time.delayedCall(80, () => target.clearTint());

  // Horizontal knockback — decelerates
  scene.tweens.add({
    targets: target,
    x: target.x + knockbackX,
    duration,
    ease: "Power2",
  });

  // Fall to ground with bounce
  scene.tweens.add({
    targets: target,
    y: groundY,
    duration,
    ease: "Bounce.easeOut",
  });

  // Tumble over
  scene.tweens.add({
    targets: target,
    angle,
    duration: duration * 0.6,
    ease: "Back.easeIn",
  });

  // Fade to ghostly after landing — stay visible
  scene.tweens.add({
    targets: target,
    alpha: 0.15,
    duration: fadeDuration,
    delay: duration + fadeDelay,
    ease: "Power2",
    onComplete: () => onComplete?.(),
  });
}
